import { useState, type FormEvent } from 'react'
import { Alert, Avatar, Button, FieldError, Input, Label, TextField } from '@heroui/react'
import { api } from '../lib/api'
import { explain } from '../lib/staffErrors'
import type { Staff } from '../lib/types'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { EntryFrame } from './EntryFrame'
import { initials } from '../lib/initials'

/**
 * Changing your own password.
 *
 * Shown as a wall rather than a setting when the account is marked: a password
 * somebody else chose is a password somebody else knows, and an account created or
 * reset by an administrator can do nothing else until its owner has changed it.
 * The wall has no top bar, because there is nowhere it could take them; the
 * voluntary change looks the same, so the one form is learnt once.
 *
 * The current password is asked for even though the person is already signed in.
 * The threat is a workstation left unlocked for two minutes, which is long enough
 * to lock the real operator out of their own account.
 */
export function ChangePassword({
  staff,
  forced,
  onDone,
  onCancel,
}: {
  staff: Staff
  forced: boolean
  onDone: () => void
  onCancel?: () => void
}) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  // A refusal is kept raw and resolved at render, so switching language from the
  // corner after it appeared shows it in the new language; the one mismatch this
  // screen finds itself is kept as its phrase for the same reason.
  const [error, setError] = useState<unknown>(null)
  const [differ, setDiffer] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (next !== again) {
      setDiffer(true)
      return
    }
    setDiffer(false)
    setBusy(true)
    try {
      await api.changePassword(current, next)
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const errorText = differ
    ? say('entry.passwordsDiffer')
    : error === null
      ? ''
      : explain(error, lang, 'common.unreachable')

  return (
    <EntryFrame>
      <div className="entry-head">
        <h1 className="entry-title">{forced ? say('entry.chooseOwnPassword') : say('entry.changePassword')}</h1>
        <p className="entry-lead">{forced ? say('entry.forcedLead') : say('entry.voluntaryLead')}</p>
      </div>
      <form className="entry-form" onSubmit={submit}>
        <div className="entry-who">
          <Avatar size="sm" variant="soft" color="accent" aria-hidden="true">
            <Avatar.Fallback>{initials(staff.displayName, staff.username)}</Avatar.Fallback>
          </Avatar>
          <span>
            {staff.displayName}
            {session?.identity.organisation && <small>{session.identity.organisation}</small>}
          </span>
        </div>

        {errorText && (
          <Alert status="danger" role="alert">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>{errorText}</Alert.Description>
            </Alert.Content>
          </Alert>
        )}

        {/* Hidden, but present: a password manager files the new entry against this username. */}
        <input type="text" name="username" autoComplete="username" value={staff.username} readOnly hidden />

        <TextField className="entry-field" name="current" type="password" value={current} onChange={setCurrent} isRequired fullWidth>
          <Label>{say('entry.currentPassword')}</Label>
          <Input autoComplete="current-password" autoFocus />
          <FieldError />
        </TextField>

        <TextField className="entry-field" name="next" type="password" value={next} onChange={setNext} minLength={8} isRequired fullWidth>
          <Label title={say('entry.atLeastEight')}>{say('entry.newPassword')}</Label>
          <Input autoComplete="new-password" />
          <FieldError />
        </TextField>

        <TextField
          className="entry-field"
          name="again"
          type="password"
          value={again}
          onChange={(v) => {
            setAgain(v)
            setDiffer(false)
          }}
          isRequired
          fullWidth
        >
          <Label>{say('entry.newPasswordAgain')}</Label>
          <Input autoComplete="new-password" aria-invalid={differ || undefined} />
          <FieldError />
        </TextField>

        <div className="entry-buttons">
          <Button type="submit" variant="primary" size="lg" fullWidth isDisabled={busy}>
            {busy ? say('common.saving') : say('entry.savePassword')}
          </Button>
          {!forced && onCancel && (
            <Button variant="tertiary" fullWidth onPress={onCancel} isDisabled={busy}>
              {say('common.cancel')}
            </Button>
          )}
          {/* The wall has no top bar: someone at the wrong account leaves from here. */}
          {forced && session && (
            <Button variant="tertiary" fullWidth onPress={session.signOut} isDisabled={busy}>
              {say('common.signOut')}
            </Button>
          )}
        </div>
      </form>
    </EntryFrame>
  )
}
